import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import FleetCarDialog, { PARTNER_CARS } from "./FleetCarDialog";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function OnRequestFleet({ background = "#F3F1EC", withTopBorder = false }) {
  const [selectedCar, setSelectedCar] = useState(null);
  const { t } = useTranslation();

  return (
    <section
      data-testid="on-request-fleet"
      className="py-24 lg:py-32 grain-overlay"
      style={{ backgroundColor: background }}
    >
      <FleetCarDialog
        car={selectedCar}
        open={!!selectedCar}
        onOpenChange={(o) => !o && setSelectedCar(null)}
      />

      <div className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-12 ${withTopBorder ? "pt-16 border-t border-[#D8D4CC]/40" : ""}`}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="mb-14 lg:mb-16 max-w-3xl"
        >
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
          >
            {t("fleet.on_request_eyebrow")}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("fleet.on_request_title_1")} <span style={{ fontWeight: 600 }}>{t("fleet.on_request_title_2")}</span>
          </h2>
          <p
            className="text-sm text-[#4A4A4A] leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            {t("fleet.on_request_note")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {PARTNER_CARS.map((car, i) => (
            <motion.div
              key={car.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`on-request-card-${car.id}`}
              className="group flex flex-col"
            >
              <button
                type="button"
                onClick={() => setSelectedCar(car)}
                data-testid={`on-request-image-${car.id}`}
                className="block w-full img-hover-zoom overflow-hidden relative text-left"
              >
                <img
                  src={car.cover}
                  alt={t(`fleet.${car.i18nKey}.name`)}
                  className="w-full h-64 lg:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B1A]/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span
                    className="inline-block bg-[#1A1B1A]/90 text-[#C4A462] px-3 py-1 text-[9px] uppercase tracking-[0.25em]"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                  >
                    {t("fleet.on_request_badge")}
                  </span>
                  <span
                    className="inline-block bg-[#FAF9F6]/90 text-[#1A1B1A] px-3 py-1 text-[9px] uppercase tracking-[0.25em]"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                  >
                    {t("fleet.partner_badge")}
                  </span>
                </div>
              </button>

              <div className="pt-5">
                <p
                  className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t(`fleet.${car.i18nKey}.year`)}
                </p>
                <h3
                  className="text-2xl lg:text-3xl tracking-tighter text-[#1A1B1A]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                >
                  {t(`fleet.${car.i18nKey}.name`)}
                </h3>
                <p
                  className="text-base text-[#4A4A4A] mt-1 italic"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                >
                  {t(`fleet.${car.i18nKey}.tagline`)}
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedCar(car)}
                  data-testid={`on-request-discover-${car.id}`}
                  className="mt-4 text-xs uppercase tracking-[0.15em] text-[#1A1B1A] hover:text-[#C4A462] inline-flex items-center gap-2 transition-colors duration-300"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("common.discover")} <ArrowRight strokeWidth={1.25} size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mt-14 lg:mt-16 text-center"
        >
          <p
            className="text-lg sm:text-xl text-[#1A1B1A] leading-relaxed max-w-2xl mx-auto mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t("fleet.on_request_any")}
          </p>
          <Link to="/contact" data-testid="on-request-cta">
            <button className="btn-luxury inline-flex items-center gap-3">
              {t("fleet.on_request_cta")} <ArrowRight strokeWidth={1.25} size={16} />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
